import React from 'react';
import { AlertTriangle } from 'lucide-react';
import './ErrorBoundary.css';

/**
 * Baustein — ErrorBoundary.
 *
 * Fängt Render-Fehler eines Teilbaums (typisch: eine Route) und zeigt eine Fallback-Karte,
 * statt die ganze App weiß zu lassen; Header und Navigation außerhalb bleiben stehen.
 *
 *   <ErrorBoundary onError={reportError} resetKey={location.pathname}>{element}</ErrorBoundary>
 *
 * Props:
 *   onError(error, info) — Meldung nach außen. Die Boundary kennt keine Reporting-Naht,
 *                          weil ui/ eine Leaf-Library ist; der Konsument reicht sie herein.
 *   resetKey             — ändert sich der Wert (z. B. Pfadwechsel), wird der Fehlerzustand
 *                          verworfen und der Teilbaum erneut gerendert.
 *
 * Klassenkomponente: React hat für getDerivedStateFromError keinen Hook-Ersatz.
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="error-boundary" role="alert">
        <AlertTriangle size={28} className="error-boundary__icon" aria-hidden />
        <h2 className="error-boundary__title">Da ist etwas schiefgelaufen</h2>
        <p className="error-boundary__text">Die Seite konnte nicht angezeigt werden. Ein Neuladen hilft meistens.</p>
        <button type="button" className="btn-secondary" onClick={() => window.location.reload()}>Seite neu laden</button>
      </div>
    );
  }
}
